package one.healthium.ui.compose.theme

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.structuralEqualityPolicy
import androidx.compose.ui.graphics.Color

val gray_1 = Color(0xFF8E8E93)
val gray_2 = Color(0xFFF8F6F4)
val gray_3 = Color(0xFF666666)
val gray_3_50 = Color(0x80666666)
val gray_4 = Color(0xFF999999)
val gray_5 = Color(0xFFEBEBEB)
val gray_6 = Color(0xFFF0EEEC)
val gray_7 = Color(0xFFF2F2F2)
val gray_7_80 = Color(0xCCF2F2F2)
val gray_8 = Color(0xFFCCCCCC)
val gray_9 = Color(0xFFF5F5F5)
val gray_10 = Color(0xFF505050)
val gray_11 = Color(0xFFD8D8D8)
val gray_12 = Color(0xFFAEAEB2)
val gray_13 = Color(0xFFEFEFF0)
val gray_14_60 = Color(0x3C3C4399)
val gray_15 = Color(0xFFE8EAED)
val gray_16_20 = Color(0x33cdcdcd)
val gray_17 = Color(0xFF9D9D9D)
val gray_18 = Color(0xFFDADADA)
val gray_19 = Color(0xFFE2E2E2)
val gray_20 = Color(0xFFD1D1D6)
val black = Color(0xFF000000)
val black10 = Color(0x1a000000)
val black15 = Color(0x26000000)
val black20 = Color(0x33000000)
val black25 = Color(0x40000000)
val black30 = Color(0x4d000000)
val black40 = Color(0x66000000)
val black45 = Color(0x73000000)
val black50 = Color(0x80000000)
val black75 = Color(0xBF000000)
val black_3 = Color(0xFF373A3C)
val black_4 = Color(0xFF333333)
val black_5 = Color(0xFF1D1C2B)
val black_6 = Color(0xFF202117)
val black_7 = Color(0xFF353727)
val black_8 = Color(0xFF4E4E4E)
val red_1 = Color(0xFFDA4960)
val red_2 = Color(0xFFDD224D)
val red_3 = Color(0xFFB33A56)
val red_4 = Color(0xFF8B273F)
val red_5 = Color(0xFFF2556F)
val red_6 = Color(0xFFCC3355)
val red_7 = Color(0xFFFF5B4D)
val red_8 = Color(0xFFBB463C)
val red_9 = Color(0xFFD75050)
val red_10 = Color(0xFF852B40)
val red_11 = Color(0xFFCC4D43)
val orange = Color(0xFFFFCC4D)
val white = Color(0xFFFFFFFF)
val white60 = Color(0x99ffffff)
val white_1 = Color(0xFFF9F9F9)
val white_1_94 = Color(0xf0f9f9f9)
val white10 = Color(0x1affffff)
val white12 = Color(0x1fffffff)
val white50 = Color(0x80ffffff)
val white75 = Color(0xBFffffff)
val yellow = Color(0xFFFFCC4D)
val yellow_2 = Color(0xFFFBE08B)
val yellow_3 = Color(0xFFFFE170)
val yellow_4 = Color(0xFFFAE16F)
val yellow_5 = Color(0xFFFFDE78)
val yellow_6 = Color(0xFFFFB701)
val yellow_7 = Color(0xFFFEC226)
val yellow_8 = Color(0xFFFFE100)
val yellow_9 = Color(0xFFFFBD6D)
val blue_1 = Color(0xFF6686DA)
val blue_2 = Color(0xFF0071CE)
val blue_3 = Color(0xFF6786DB)
val blue_4 = Color(0xFFCAEAEC)
val blue_5 = Color(0xFF6E64D2)
val blue_6 = Color(0xFF4788FF)
val green_1 = Color(0xFF80B40A)
val green_2 = Color(0xFF5A831D)
val green_3 = Color(0xFFA3D544)
val green_4 = Color(0xFF93BF45)
val green_5 = Color(0xFFA9D260)
val green_6 = Color(0xFFDEE37C)
val green_7 = Color(0xFF10B981)
val green_8 = Color(0xFF00C27C)
val green_9 = Color(0xFF60CCCC)
val green_10 = Color(0xFFAFEA0D)
val green_11 = Color(0xFF173430)
val green_11_15 = Color(0x26173430)
val green_11_50 = Color(0x80173430)
val green_12 = Color(0xFF9ED031)
val green_13 = Color(0xFFEBF6D6)
val green_14 = Color(0xFFCAECDA)
val orange_1 = Color(0xFFE46214)
val orange_2 = Color(0xFFF57600)
val orange_3 = Color(0xFFF58722)
val orange_4 = Color(0xFFFF974D)
val brown_1 = Color(0xFF9A5D41)
val brown_2 = Color(0xFF31231B)
val pink_1 = Color(0xFFE98FA9)
val pink_2 = Color(0xFFFED5BE)
val pink_3 = Color(0xFFFEDEBE)
val pink_4 = Color(0xFFFEF2F2)
val purple_1 = Color(0xFF8E72B1)

@Stable
class AppColors(
    primary: Color,
    onPrimary: Color,
    secondary: Color,
    tertiary: Color,
    onSecondary: Color,
    surface: Color,
    onSurface: Color,
    windowBackground: Color,
    divider: Color,
    text100: Color,
    text200: Color,
    text300: Color,
    text400: Color,
    text500: Color,
    textInverted100: Color,
) {
    var primary by mutableStateOf(primary, structuralEqualityPolicy())
        internal set

    var onPrimary by mutableStateOf(onPrimary, structuralEqualityPolicy())
        internal set

    var secondary by mutableStateOf(secondary, structuralEqualityPolicy())
        internal set

    var tertiary by mutableStateOf(tertiary, structuralEqualityPolicy())
        internal set

    var onSecondary by mutableStateOf(onSecondary, structuralEqualityPolicy())
        internal set

    var surface by mutableStateOf(surface, structuralEqualityPolicy())
        internal set

    var onSurface by mutableStateOf(onSurface, structuralEqualityPolicy())
        internal set

    var windowBackground by mutableStateOf(windowBackground, structuralEqualityPolicy())
        internal set

    var divider by mutableStateOf(divider, structuralEqualityPolicy())
        internal set

    var text100 by mutableStateOf(text100, structuralEqualityPolicy())
        internal set

    var text200 by mutableStateOf(text200, structuralEqualityPolicy())
        internal set

    var text300 by mutableStateOf(text300, structuralEqualityPolicy())
        internal set

    var text400 by mutableStateOf(text400, structuralEqualityPolicy())
        internal set

    var text500 by mutableStateOf(text500, structuralEqualityPolicy())
        internal set

    var textInverted100 by mutableStateOf(textInverted100, structuralEqualityPolicy())
        internal set
}

object Colors {
    val ifa = AppColors(
        primary = red_2,
        onPrimary = white,
        secondary = orange_2,
        tertiary = orange_2,
        onSecondary = white,
        surface = white,
        onSurface = black_3,
        windowBackground = white,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val carnivore = AppColors(
        primary = orange_2,
        onPrimary = white,
        secondary = red_2,
        onSecondary = white,
        tertiary = orange_2,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val pcos = AppColors(
        primary = red_3,
        onPrimary = white,
        secondary = red_6,
        onSecondary = white,
        tertiary = white,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val med = AppColors(
        primary = green_5,
        onPrimary = white,
        secondary = green_4,
        onSecondary = white,
        tertiary = white,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val pilates = AppColors(
        primary = blue_5,
        onPrimary = white,
        secondary = blue_5,
        onSecondary = white,
        tertiary = blue_5,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val fodmap = AppColors(
        primary = blue_6,
        onPrimary = white,
        secondary = orange,
        onSecondary = blue_6,
        tertiary = blue_6,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val rice = AppColors(
        primary = green_9,
        onPrimary = white,
        secondary = green_9,
        onSecondary = white,
        tertiary = yellow,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val bland = AppColors(
        primary = red_7,
        onPrimary = white,
        secondary = yellow_9,
        onSecondary = black,
        tertiary = red_7,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val cali = AppColors(
        primary = green_10,
        onPrimary = black_6,
        secondary = yellow_9,
        onSecondary = black,
        tertiary = red_7,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )

    val life = AppColors(
        primary = green_11,
        onPrimary = white,
        secondary = green_12,
        onSecondary = black_6,
        tertiary = green_12,
        surface = white,
        onSurface = black_3,
        windowBackground = gray_2,
        divider = white_1_94,
        text100 = black,
        text200 = blue_1,
        text300 = gray_4,
        text400 = black_4,
        text500 = black_3,
        textInverted100 = white,
    )
}